import { useState, useMemo } from 'react'
import { Loader2, ListTodo, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui'
import { TaskCard } from './TaskCard'
import { TaskFilters } from './TaskFilters'
import { useTasks } from '@/hooks'
import type { Task, TaskStatus, TaskPriority, TaskFilters as TaskFiltersType } from '@/api'

const PAGE_SIZE = 20

interface TaskListProps {
  onTaskSelect?: (task: Task) => void
  selectedTaskId?: string | null
  showFilters?: boolean
  initialFilters?: TaskFiltersType
}

export function TaskList({
  onTaskSelect,
  selectedTaskId,
  showFilters = true,
  initialFilters,
}: TaskListProps) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<TaskStatus | null>(
    initialFilters?.status || null
  )
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | null>(
    initialFilters?.priority || null
  )

  // Build query filters
  const filters: TaskFiltersType = useMemo(() => ({
    ...initialFilters,
    status: statusFilter || undefined,
    priority: priorityFilter || undefined,
    page,
    per_page: PAGE_SIZE,
  }), [initialFilters, statusFilter, priorityFilter, page])

  const { data, isLoading, isError, error } = useTasks(filters)

  // Client-side search filter
  const filteredTasks = useMemo(() => {
    if (!data?.items) return []
    if (!search) return data.items

    const searchLower = search.toLowerCase()
    return data.items.filter(task =>
      task.title.toLowerCase().includes(searchLower) ||
      task.client_name?.toLowerCase().includes(searchLower) ||
      task.assignee_name?.toLowerCase().includes(searchLower) ||
      task.statutory_type?.toLowerCase().includes(searchLower)
    )
  }, [data?.items, search])

  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / PAGE_SIZE)

  const clearFilters = () => {
    setSearch('')
    setStatusFilter(null)
    setPriorityFilter(null)
    setPage(1)
  }

  // Reset to page 1 when filters change
  const handleStatusChange = (v: TaskStatus | null) => { setStatusFilter(v); setPage(1) }
  const handlePriorityChange = (v: TaskPriority | null) => { setPriorityFilter(v); setPage(1) }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-destructive">Failed to load tasks</p>
        <p className="text-sm text-muted-foreground mt-1">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {showFilters && (
        <TaskFilters
          search={search}
          onSearchChange={setSearch}
          statusFilter={statusFilter}
          onStatusChange={handleStatusChange}
          priorityFilter={priorityFilter}
          onPriorityChange={handlePriorityChange}
          onClearFilters={clearFilters}
        />
      )}

      {filteredTasks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <ListTodo className="h-12 w-12 text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground">No tasks found</p>
          {(search || statusFilter || priorityFilter) && (
            <p className="text-sm text-muted-foreground mt-1">
              Try adjusting your filters
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onClick={() => onTaskSelect?.(task)}
              selected={selectedTaskId === task.id}
            />
          ))}
        </div>
      )}

      {/* Pagination footer */}
      {total > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {total <= PAGE_SIZE
              ? `${total} tasks`
              : `Page ${page} of ${totalPages} (${total} tasks)`}
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
